import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUser,
  faSignOutAlt,
  faBell,
  faCalendarAlt,
  faInfoCircle,
  faGraduationCap,
  faStar,
  faBullhorn,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import '../../css/JuanIS/JuanISFacultyDashboard.css';
import { useNavigate, useLocation } from 'react-router-dom';
import SJDEFILogo from '../../images/SJDEFILogo.png';
import JuanISFacultyClassSchedule from './JuanISFacultyClassSchedule';
import JuanISFacultyClassInformation from './JuanISFacultyClassInformation';
import JuanISFacultyClassGrades from './JuanISFacultyGrades';
import JuanISFacultyOTE from './JuanISFacultyOTE';
import JuanISFacultyAnnouncements from './JuanISFacultyAnnouncements';
import JuanISFacultyProfile from './JuanISFacultyProfile';

function JuanISFacultyDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('home');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Retrieve user data from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      // Redirect to login if no user data
      navigate('/juan-is-login');
    }
  }, [navigate]);

  // Fallback to location.state if user is not set yet
  const email = user?.email || location.state?.email || 'faculty@sjdefi.edu.ph';
  const employeeID = user?.employeeID || location.state?.employeeID;
  const firstName = user?.firstName || location.state?.firstName || 'Faculty';
  const lastName = user?.lastName || location.state?.lastName || 'User';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  // Generate initials based on email
  const getEmailInitials = (email) => {
    const username = email.split('@')[0];
    return username.length >= 2 ? username.slice(0, 2).toUpperCase() : username.toUpperCase();
  };

  // Mask email for display
  const maskEmail = (email) => {
    const [username, domain] = email.split('@');
    if (username.length <= 4) {
      return `${username[0]}***@${domain}`;
    }
    return `${username.slice(0, 4)}****@${domain}`;
  };

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/juan-is-login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleNotificationClick = () => {
    setActiveTab('announcements');
    setIsSidebarOpen(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'announcements':
        return <JuanISFacultyAnnouncements email={email} />;
      case 'class-schedule':
        return <JuanISFacultyClassSchedule />;
      case 'class-information':
        return <JuanISFacultyClassInformation />;
      case 'handling-of-grades':
        return <JuanISFacultyClassGrades />;
      case 'viewing-of-ote':
        return <JuanISFacultyOTE />;
      case 'profile':
        return (
          <JuanISFacultyProfile
            employeeID={employeeID}
            initialProfile={{
              fullName,
              employeeID,
              email,
            }}
          />
        );
      default:
        return (
          <>
            <div className="juanis-faculty-dashboard-welcome-banner">
              <h1>Welcome to JuanIS</h1>
              <div className="juanis-faculty-dashboard-welcome-icon">
                <FontAwesomeIcon icon={faUser} size="2x" />
              </div>
            </div>

            <div className="juanis-faculty-dashboard-content-section">
              <h2>Quick Access</h2>
              <div className="juanis-faculty-dashboard-quick-access-grid">
                <div className="juanis-faculty-dashboard-quick-access-card" onClick={() => setActiveTab('class-schedule')}>
                  <div className="juanis-faculty-dashboard-icon-container">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <span>Class Schedule</span>
                </div>
                <div className="juanis-faculty-dashboard-quick-access-card" onClick={() => setActiveTab('class-information')}>
                  <div className="juanis-faculty-dashboard-icon-container">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </div>
                  <span>Class Information</span>
                </div>
                <div className="juanis-faculty-dashboard-quick-access-card" onClick={() => setActiveTab('handling-of-grades')}>
                  <div className="juanis-faculty-dashboard-icon-container">
                    <FontAwesomeIcon icon={faGraduationCap} />
                  </div>
                  <span>Handling of Grades</span>
                </div>
                <div className="juanis-faculty-dashboard-quick-access-card" onClick={() => setActiveTab('viewing-of-ote')}>
                  <div className="juanis-faculty-dashboard-icon-container">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <span>Viewing of OTE</span>
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  if (!user && !location.state?.email) {
    return null; // Render nothing while redirecting
  }

  return (
    <div className="juanis-faculty-dashboard-container">
      {/* Header */}
      <header className="juanis-faculty-dashboard-header">
        <div className="juanis-faculty-dashboard-header-logo">
          <img src={SJDEFILogo} alt="SJDEFI Logo" />
          <span>JUAN IS</span>
        </div>
        <div className="juanis-faculty-dashboard-header-right">
          <div className="juanis-faculty-dashboard-notification-icon" onClick={handleNotificationClick}>
            <FontAwesomeIcon icon={faBell} />
          </div>
          <button className="juanis-faculty-dashboard-sidebar-toggle" onClick={toggleSidebar}>
            <FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} />
          </button>
        </div>
      </header>

      <div className="juanis-faculty-dashboard-content">
        {/* Left Sidebar */}
        <div className={`juanis-faculty-dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="juanis-faculty-dashboard-user-profile">
            <div className="juanis-faculty-dashboard-avatar">
              <div 
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: '#4a90e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}
              >
                {getEmailInitials(email)}
              </div>
            </div>
            <div className="juanis-faculty-dashboard-user-info">
              <p className="juanis-faculty-dashboard-user-email">{maskEmail(email)}</p>
              <p className="juanis-faculty-dashboard-user-type">Faculty</p>
            </div>
          </div>

          <div className="juanis-faculty-dashboard-nav-section">
            <div className="juanis-faculty-dashboard-section-title">Main</div>
            <ul className="juanis-faculty-dashboard-nav-menu">
              <li className={activeTab === 'home' ? 'active' : ''} onClick={() => { setActiveTab('home'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faHome} style={activeTab === 'home' ? {color: '#FFB800'} : {}} />
                <span>Home</span>
              </li>
              <li className={activeTab === 'announcements' ? 'active' : ''} onClick={() => { setActiveTab('announcements'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faBullhorn} style={activeTab === 'announcements' ? {color: '#FFB800'} : {}} />
                <span>Announcements</span>
              </li>
              <li className={activeTab === 'profile' ? 'active' : ''} onClick={() => { setActiveTab('profile'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faUser} style={activeTab === 'profile' ? {color: '#FFB800'} : {}} />
                <span>Profile</span>
              </li>
            </ul>
          </div>

          <div className="juanis-faculty-dashboard-nav-section">
            <div className="juanis-faculty-dashboard-section-title">Quick Access</div>
            <ul className="juanis-faculty-dashboard-nav-menu">
              <li className={activeTab === 'class-schedule' ? 'active' : ''} onClick={() => { setActiveTab('class-schedule'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faCalendarAlt} style={activeTab === 'class-schedule' ? {color: '#FFB800'} : {}} />
                <span>Class Schedule</span>
              </li>
              <li className={activeTab === 'class-information' ? 'active' : ''} onClick={() => { setActiveTab('class-information'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faInfoCircle} style={activeTab === 'class-information' ? {color: '#FFB800'} : {}} />
                <span>Class Information</span>
              </li>
              <li className={activeTab === 'handling-of-grades' ? 'active' : ''} onClick={() => { setActiveTab('handling-of-grades'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faGraduationCap} style={activeTab === 'handling-of-grades' ? {color: '#FFB800'} : {}} />
                <span>Handling of Grades</span>
              </li>
              <li className={activeTab === 'viewing-of-ote' ? 'active' : ''} onClick={() => { setActiveTab('viewing-of-ote'); setIsSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faStar} style={activeTab === 'viewing-of-ote' ? {color: '#FFB800'} : {}} />
                <span>Viewing of OTE</span>
              </li>
            </ul>
          </div>

          <button className="juanis-faculty-dashboard-logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="juanis-faculty-dashboard-main-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default JuanISFacultyDashboard;